import React, { useState } from 'react';
import {
  IonContent,
  IonPage,
  IonInput,
  IonButton,
  IonIcon,
  IonCheckbox,
  IonSpinner,
  IonToast,
  IonItem,
  IonModal,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonButtons,
} from '@ionic/react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  mailOutline,
  lockClosedOutline,
  logoGoogle,
  informationCircleOutline,
  closeOutline,
  keyOutline,
  checkmarkCircleOutline,
} from 'ionicons/icons';
import { useAuth } from '../contexts/AuthContext';
import ManageTixLogo from '../components/ManageTixLogo';

const Login: React.FC = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const { login, loginWithGoogle, resetPassword, error, clearError } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [showAboutModal, setShowAboutModal] = useState(false);
  const [showResetModal, setShowResetModal] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetLoading, setResetLoading] = useState(false);
  const [resetSuccess, setResetSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate inputs
    if (!email || !password) {
      console.error('Missing email or password', { email, password });
      setShowToast(true);
      return;
    }

    setLoading(true);

    try {
      await login(email, password);
      history.push('/dashboard');
    } catch (error) {
      console.error('Login error:', error);
      setShowToast(true);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    setLoading(true);
    try {
      await loginWithGoogle();
      history.push('/dashboard');
    } catch (error) {
      setShowToast(true);
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!resetEmail) {
      setShowToast(true);
      return;
    }

    setResetLoading(true);
    try {
      await resetPassword(resetEmail);
      setResetSuccess(true);
      setTimeout(() => {
        setShowResetModal(false);
        setResetSuccess(false);
        setResetEmail('');
      }, 3000);
    } catch (error) {
      setShowToast(true);
    } finally {
      setResetLoading(false);
    }
  };

  const openResetModal = () => {
    setResetEmail(email); // Pre-fill with login email if available
    setShowResetModal(true);
    setResetSuccess(false);
  };

  return (
    <IonPage>
      <IonContent
        fullscreen
        className="bg-gradient-to-br from-primary-600 to-purple-700"
      >
        <div className="min-h-screen flex items-center justify-center px-4 py-8">
          <div className="max-w-md w-full">
            <div className="bg-white/95 dark:bg-gray-900/95 backdrop-blur-md shadow-2xl rounded-2xl overflow-hidden">
              <div className="bg-gradient-to-r from-primary-500 to-primary-700 text-white px-8 py-6">
                <div className="flex justify-center mb-4">
                  <ManageTixLogo size={64} showText={false} />
                </div>
                <h1 className="text-3xl font-bold text-center mb-2">
                  ManageTix
                </h1>
                <p className="text-sm text-center opacity-90 mb-2">
                  Your comprehensive ticket inventory and sales management
                  platform
                </p>
                <p className="text-lg text-center opacity-90">
                  {t('auth.login')}
                </p>
                <div className="text-center mt-3">
                  <IonButton
                    fill="clear"
                    size="small"
                    onClick={() => setShowAboutModal(true)}
                    className="text-white/80 hover:text-white"
                  >
                    <IonIcon icon={informationCircleOutline} slot="start" />
                    About Us
                  </IonButton>
                </div>
              </div>

              <div className="p-8">
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="space-y-4">
                    <div className="relative group">
                      <IonItem
                        lines="none"
                        className="border-2 border-gray-200 dark:border-gray-700 rounded-lg transition-all duration-200 hover:border-primary-400 focus-within:border-primary-500 focus-within:shadow-lg"
                      >
                        <IonIcon
                          icon={mailOutline}
                          slot="start"
                          className="text-gray-500 dark:text-gray-400 mr-3"
                        />
                        <IonInput
                          type="email"
                          value={email}
                          onIonChange={(e) => setEmail(e.detail.value!)}
                          onIonInput={(e: any) =>
                            setEmail(e.target.value || '')
                          }
                          placeholder={t('auth.email')}
                          required
                          autocomplete="email"
                          className="text-gray-900 dark:text-gray-100"
                        />
                      </IonItem>
                    </div>

                    <div className="relative group">
                      <IonItem
                        lines="none"
                        className="border-2 border-gray-200 dark:border-gray-700 rounded-lg transition-all duration-200 hover:border-primary-400 focus-within:border-primary-500 focus-within:shadow-lg"
                      >
                        <IonIcon
                          icon={lockClosedOutline}
                          slot="start"
                          className="text-gray-500 dark:text-gray-400 mr-3"
                        />
                        <IonInput
                          type="password"
                          value={password}
                          onIonChange={(e) => setPassword(e.detail.value!)}
                          onIonInput={(e: any) =>
                            setPassword(e.target.value || '')
                          }
                          placeholder={t('auth.password')}
                          required
                          autocomplete="current-password"
                          className="text-gray-900 dark:text-gray-100"
                        />
                      </IonItem>
                    </div>

                    <div className="flex items-center justify-between">
                      <label className="flex items-center space-x-2 cursor-pointer">
                        <IonCheckbox
                          checked={rememberMe}
                          onIonChange={(e) => setRememberMe(e.detail.checked)}
                          className="rounded"
                        />
                        <span className="text-sm text-gray-600 dark:text-gray-400">
                          {t('auth.rememberMe')}
                        </span>
                      </label>
                      <button
                        type="button"
                        onClick={openResetModal}
                        className="text-sm text-primary-600 hover:text-primary-700 transition-colors underline"
                      >
                        {t('auth.forgotPassword')}
                      </button>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <IonButton
                      expand="block"
                      type="submit"
                      disabled={loading}
                      className="bg-gradient-to-r from-primary-600 to-primary-700 text-white font-semibold py-3 px-4 rounded-lg hover:from-primary-700 hover:to-primary-800 transform transition-all duration-200 hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {loading ? (
                        <IonSpinner name="crescent" className="w-5 h-5" />
                      ) : (
                        t('auth.login')
                      )}
                    </IonButton>

                    <IonButton
                      expand="block"
                      fill="clear"
                      onClick={openResetModal}
                      disabled={loading}
                      className="text-sm text-gray-600 dark:text-gray-400 hover:text-primary-600 transition-colors"
                    >
                      <IonIcon icon={keyOutline} slot="start" className="text-lg" />
                      Reset Password
                    </IonButton>

                    <div className="relative">
                      <div className="absolute inset-0 flex items-center">
                        <div className="w-full border-t border-gray-300 dark:border-gray-600"></div>
                      </div>
                      <div className="relative flex justify-center text-sm">
                        <span className="px-2 bg-white dark:bg-gray-900 text-gray-500">
                          {t('common.or') || 'Or'}
                        </span>
                      </div>
                    </div>

                    {/* <IonButton
                      expand="block"
                      fill="outline"
                      onClick={handleGoogleLogin}
                      disabled={loading}
                      className="border-2 border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500 transition-colors bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-300"
                    >
                      <IonIcon
                        icon={logoGoogle}
                        slot="start"
                        className="text-xl mr-2"
                      />
                      {t('auth.loginWithGoogle')}
                    </IonButton> */}
                  </div>

                  <div className="text-center text-sm text-gray-600 dark:text-gray-400">
                    {t('auth.noAccount')}{' '}
                    <a
                      href="/signup"
                      className="font-medium text-primary-600 hover:text-primary-500 transition-colors"
                    >
                      {t('auth.signup')}
                    </a>
                  </div>

                  <div className="text-center text-xs text-gray-500 dark:text-gray-500 pt-4 border-t border-gray-200 dark:border-gray-700">
                    By logging in, you agree to our{' '}
                    <a
                      href="/terms"
                      className="font-medium text-primary-600 hover:text-primary-500 transition-colors underline"
                    >
                      Terms of Service
                    </a>{' '}
                    and{' '}
                    <a
                      href="/privacy"
                      className="font-medium text-primary-600 hover:text-primary-500 transition-colors underline"
                    >
                      Privacy Policy
                    </a>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>

        <IonToast
          isOpen={showToast}
          onDidDismiss={() => {
            setShowToast(false);
            clearError();
          }}
          message={error || t('auth.loginError')}
          duration={3000}
          color="danger"
          position="top"
        />

        {/* Reset Password Modal */}
        <IonModal
          isOpen={showResetModal}
          onDidDismiss={() => {
            setShowResetModal(false);
            setResetSuccess(false);
            setResetEmail('');
          }}
        >
          <IonHeader>
            <IonToolbar>
              <IonTitle>Reset Password</IonTitle>
              <IonButtons slot="end">
                <IonButton onClick={() => setShowResetModal(false)}>
                  <IonIcon icon={closeOutline} />
                </IonButton>
              </IonButtons>
            </IonToolbar>
          </IonHeader>
          <IonContent className="ion-padding">
            {resetSuccess ? (
              <div className="text-center py-12">
                <IonIcon 
                  icon={checkmarkCircleOutline} 
                  className="text-6xl text-green-500 mb-4" 
                />
                <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">
                  Reset Email Sent!
                </h2>
                <p className="text-gray-600 dark:text-gray-400 mb-4">
                  We've sent a password reset link to <strong>{resetEmail}</strong>
                </p>
                <p className="text-sm text-gray-500 dark:text-gray-500">
                  Check your email and follow the instructions to reset your password.
                </p>
              </div>
            ) : (
              <form onSubmit={handleResetPassword} className="space-y-6">
                <div className="text-center mb-6">
                  <IonIcon icon={keyOutline} className="text-4xl text-primary-600 mb-3" />
                  <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">
                    Reset Your Password
                  </h2>
                  <p className="text-gray-600 dark:text-gray-400">
                    Enter your email address and we'll send you a link to reset your password.
                  </p>
                </div>

                <div className="space-y-4">
                  <IonItem
                    lines="none"
                    className="border-2 border-gray-200 dark:border-gray-700 rounded-lg"
                  >
                    <IonIcon
                      icon={mailOutline}
                      slot="start"
                      className="text-gray-500 dark:text-gray-400"
                    />
                    <IonInput
                      type="email"
                      value={resetEmail}
                      onIonChange={(e) => setResetEmail(e.detail.value!)}
                      placeholder="Enter your email address"
                      required
                      autocomplete="email"
                      className="text-gray-900 dark:text-gray-100"
                    />
                  </IonItem>
                </div>

                <div className="space-y-3">
                  <IonButton
                    expand="block"
                    type="submit"
                    disabled={resetLoading || !resetEmail}
                    className="bg-gradient-to-r from-primary-600 to-primary-700"
                  >
                    {resetLoading ? (
                      <>
                        <IonSpinner name="crescent" className="mr-2" />
                        Sending Reset Email...
                      </>
                    ) : (
                      'Send Reset Email'
                    )}
                  </IonButton>

                  <IonButton
                    expand="block"
                    fill="clear"
                    onClick={() => setShowResetModal(false)}
                    className="text-gray-600"
                  >
                    Cancel
                  </IonButton>
                </div>
              </form>
            )}
          </IonContent>
        </IonModal>

        {/* About Us Modal */}
        <IonModal
          isOpen={showAboutModal}
          onDidDismiss={() => setShowAboutModal(false)}
        >
          <IonHeader>
            <IonToolbar>
              <IonTitle>About ManageTix</IonTitle>
              <IonButtons slot="end">
                <IonButton onClick={() => setShowAboutModal(false)}>
                  <IonIcon icon={closeOutline} />
                </IonButton>
              </IonButtons>
            </IonToolbar>
          </IonHeader>
          <IonContent className="ion-padding">
            <div className="space-y-6">
              <div>
                <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-4">
                  Welcome to ManageTix
                </h2>
                <p className="text-gray-700 dark:text-gray-300 mb-4">
                  ManageTix is a comprehensive ticket management platform
                  designed specifically for ticket traders and resellers in the
                  sports and entertainment industry.
                </p>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-3">
                  What We Offer
                </h3>
                <ul className="list-disc pl-6 space-y-2 text-gray-700 dark:text-gray-300">
                  <li>
                    <strong>Inventory Management:</strong> Track your ticket
                    inventory across multiple events, categories, and venues
                    with real-time availability updates.
                  </li>
                  <li>
                    <strong>Sales Tracking:</strong> Record and monitor all your
                    ticket sales with detailed buyer information, pricing, and
                    profit calculations.
                  </li>
                  <li>
                    <strong>Multi-Source Import:</strong> Import sales data from
                    LiveFootballTickets, StubHub, and FootballTicketNet with
                    automatic parsing and categorization.
                  </li>
                  <li>
                    <strong>Premier League Integration:</strong> Access live
                    match data for Premier League and other major football
                    competitions to quickly add upcoming events.
                  </li>
                  <li>
                    <strong>Smart Analytics:</strong> Generate detailed reports
                    on sales performance, profit margins, and inventory turnover
                    to optimize your business.
                  </li>
                  <li>
                    <strong>Email Integration:</strong> Automatically import
                    sales from your Gmail account with intelligent parsing of
                    order confirmations.
                  </li>
                </ul>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-3">
                  Why Choose ManageTix?
                </h3>
                <p className="text-gray-700 dark:text-gray-300 mb-3">
                  Built by ticket traders for ticket traders, ManageTix
                  understands the unique challenges of managing ticket inventory
                  and sales across multiple platforms. Our platform helps you:
                </p>
                <ul className="list-disc pl-6 space-y-2 text-gray-700 dark:text-gray-300">
                  <li>
                    Save time with automated data import and categorization
                  </li>
                  <li>Reduce errors with centralized inventory management</li>
                  <li>
                    Maximize profits with real-time analytics and insights
                  </li>
                  <li>
                    Stay organized with comprehensive tracking and reporting
                  </li>
                </ul>
              </div>

              <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
                <p className="text-sm text-gray-600 dark:text-gray-400 text-center">
                  © 2024 ManageTix. All rights reserved.
                </p>
              </div>
            </div>
          </IonContent>
        </IonModal>
      </IonContent>
    </IonPage>
  );
};

export default Login;
